import { findLastIndex } from 'lodash'
import { Node, XYPosition } from 'reactflow'
import { v4 } from 'uuid'

import { ActionFeature } from '@/components/Icons'
import {
  StyledArrowsOpposite,
  StyledPaperPlaneIcon,
  StyledScheduledIcon
} from '@/pages/SpaceAppBuilder/components/StyledComponents'
import {
  CustomNodes,
  DataSourceEventTrigger,
  FlowElement,
  Operation,
  PlaceholderNodeTypes,
  Step,
  StepType,
  Triggers,
  TriggerTypes,
  Workflow,
  WorkgridCatalogDatasource
} from '@/pages/SpaceAppBuilder/utilities/interfaces'

import FeatureTriggerIcon from './images/operations/FeatureTriggerIcon'
import KnowledgeIcon from './images/operations/KnowledgeIcon'
import UIView from './images/operations/UIView'
import messages from './messages'
import getOperations from './Operations/getOperations'
import operationMessages from './Operations/messages'

interface PlaceholderNodeParams {
  position: XYPosition
  previousStep?: string | undefined
  nextStep?: Step | undefined
  parentStep?: string | undefined
  isNestedEndPlaceholder?: boolean | undefined
  type?: string | undefined
}

export const DATA_NODE_CENTER_WIDTH = 132

const constructPlaceholderNode = ({ position, type, previousStep, parentStep, nextStep }: PlaceholderNodeParams) => {
  const verticalOffset = 130
  return {
    id: v4(),
    type: CustomNodes.placeHolderNode,
    position: {
      x: position.x + DATA_NODE_CENTER_WIDTH,
      y: position.y + verticalOffset
    },
    connectable: false,
    draggable: false,
    data: {
      previousStep,
      nextStep,
      parentStep,
      type
    },
    style: {
      padding: 0
    }
  }
}
export const triggersDescriptionAndIcon = (trigger: Triggers) => {
  const types = {
    [TriggerTypes.manual]: {
      description: operationMessages.manualTriggerDescription,
      icon: <StyledPaperPlaneIcon />
    },
    [TriggerTypes.scheduled]: {
      description: operationMessages.scheduledTriggerDescription,
      icon: <StyledScheduledIcon />
    },
    [TriggerTypes.dataSourceEvent]: {
      iconUrl: (trigger as DataSourceEventTrigger)?.triggerData?.triggerSourceImageUrl
    },
    [TriggerTypes.none]: {},
    [TriggerTypes.notificationAction]: {
      icon: <ActionFeature />
    },
    [TriggerTypes.http]: {
      icon: <StyledArrowsOpposite />
    },
    [TriggerTypes.featureTrigger]: {
      description: operationMessages.runFeatureTriggerDescription,
      icon: <FeatureTriggerIcon />
    },
    [TriggerTypes.microappView]: {
      icon: <UIView />
    },
    [TriggerTypes.knowledge]: {
      icon: <KnowledgeIcon />
    }
  }
  return types[trigger.type]
}

const constructTriggerNode = (trigger: Triggers, startingStep: Step) => {
  const triggerType = triggersDescriptionAndIcon(trigger)
  return {
    id: 'trigger',
    type: CustomNodes.triggerNode,
    data: {
      label: 'Trigger',
      name: trigger.name,
      title: messages.start,
      config: trigger,
      ...triggerType,
      ...(startingStep && { onSuccess: [startingStep.id] })
    },
    connectable: true,
    draggable: false,
    style: {
      border: `2px solid ${showErrorState(trigger) ? 'var(--wg-red)' : '#ececec'}`,
      backgroundColor: '#ffffff',
      padding: 0,
      borderRadius: 8
    },
    position: { x: 300, y: 70 }
  }
}

const constructStepNode = (
  step: Step,
  position: XYPosition | undefined,
  selectedOperation: Operation,
  index: number,
  dataSources: WorkgridCatalogDatasource[]
) => {
  const dataSourceProps = {} as any
  if (step.type === StepType.DataSource || step.type === StepType.WorkgridDataSource) {
    const dataSource = dataSources.find(ds => ds.id === step.stepData.dataSourceKey)
    dataSourceProps.iconUrl = dataSource?.iconUrl || ''
    dataSourceProps.vendorName = dataSource?.name
  }
  return {
    id: step.id || v4(),
    type: CustomNodes.stepNode,
    position: position || { x: 0, y: 0 },
    connectable: true,
    draggable: false,
    style: {
      border: `2px solid ${
        showErrorState(step) ? 'var(--wg-red)' : showValidExecutionState(step) ? 'var(--wg-green)' : '#DDDDDD'
      }`,
      backgroundColor: '#ffffff',
      boxShadow: 'none',
      padding: 0,
      borderRadius: 8
    },
    data: {
      label: `Step ${index}`,
      name: step.name,
      title: selectedOperation.label,
      description: selectedOperation.description,
      icon: selectedOperation.icon,
      ...dataSourceProps,
      config: step,
      onSuccess: step.onSuccess
    }
  }
}

interface VisualOperationForStep {
  elements: Node[]
  step: Step
  steps: Step[]
}
interface VisualOperationForStepResponse {
  position: XYPosition
  selectedOperation: Operation | undefined
}

const getStepOperation = ({ elements, step }: VisualOperationForStep): VisualOperationForStepResponse => {
  const lastElement = elements.at(-1)!
  const selectedOperation = getOperations().find(operation => {
    const isSameType = operation?.name === step.type
    // TODO: update this to be generic
    if (step.type === StepType.DataTransform && step.stepData.actionName) {
      const isSameAction = operation.defaultStepData?.actionName === step.stepData.actionName
      return isSameType && isSameAction
    }
    return isSameType
  }) as unknown as Operation
  const centerLastElement = lastElement.position.x - DATA_NODE_CENTER_WIDTH
  const verticalOffset = lastElement.position.y + 80

  const position: XYPosition = {
    x: centerLastElement + (selectedOperation?.horizontalOffset || 0),
    y: verticalOffset + (selectedOperation?.verticalOffset || 0)
  }

  return { position, selectedOperation }
}

const getMaxDepthLevel = (currentStep: Step) => {
  let nestedLeftNodeCount = 1
  let nestedRightNodeCount = 1
  const countNestedOperations = (steps: Step[], direction?: string) => {
    const nestedStep = steps.find(step => step.type === StepType.Boolean || step.type === StepType.Loop)
    if (nestedStep) {
      if (direction === 'left') {
        nestedLeftNodeCount++
      }
      if (direction === 'right') {
        nestedRightNodeCount++
      }
      if (nestedStep.type === StepType.Boolean) {
        countNestedOperations(nestedStep.stepData.onTrue, 'right')
        countNestedOperations(nestedStep.stepData.onFalse, 'left')
      } else {
        countNestedOperations(nestedStep.stepData.loopSteps, 'right')
      }
    }
  }
  countNestedOperations([currentStep])
  return Math.max(nestedLeftNodeCount, nestedRightNodeCount)
}

const getLastNestedPlaceholderNode = (
  currentStep: Step,
  nodes: Node[],
  placeholderNode: Node,
  nestedStepKey: string
) => {
  if (currentStep.stepData[nestedStepKey].length > 0) {
    const lastStep = currentStep.stepData[nestedStepKey].at(-1)
    const lastStepIndex = nodes.findIndex(ele => ele.id === lastStep.id)
    if (lastStep.type === StepType.Response) {
      return null
    } else if (lastStep.type === StepType.Boolean || lastStep.type === StepType.Loop) {
      const lastNestedElement = findLastIndex(nodes, ele => ele.data.parentStep === lastStep.id)
      return nodes[lastNestedElement + 1]
    } else {
      return nodes[lastStepIndex + 1]
    }
  } else {
    return placeholderNode
  }
}

const getLastNestedNode = (step: Step) => {
  let lastOnTrue = step
  let lastOnFalse = step

  // Traverse the onTrue chain if it exists
  while (lastOnTrue.stepData.onTrue && lastOnTrue.stepData.onTrue.length > 0) {
    lastOnTrue = lastOnTrue.stepData.onTrue.at(-1)
  }

  // Traverse the onFalse chain if it exists
  while (lastOnFalse.stepData.onFalse && lastOnFalse.stepData.onFalse.length > 0) {
    lastOnFalse = lastOnFalse.stepData.onFalse.at(-1)
  }

  return { lastOnTrue, lastOnFalse }
}

const isLastNodeOfTypeResponse = (step: Step) => {
  const { lastOnTrue, lastOnFalse } = getLastNestedNode(step)

  const isLastOnTrueResponseStep = lastOnTrue.type === StepType.Response
  const isLastOnFalseResponseStep = lastOnFalse.type === StepType.Response

  return { isLastOnTrueResponseStep, isLastOnFalseResponseStep }
}

const showErrorState = (obj: any) => {
  return obj.errorConfig?.hasErrors && !obj.errorConfig?.showValid
}

const showValidExecutionState = (obj: any) => {
  return obj.errorConfig?.showValidExecution
}

interface ConvertFeatureToVisualWorkflowParams {
  workflow: Workflow
  dataSources: WorkgridCatalogDatasource[]
  isValidatingFeature: boolean
}

export const convertFeatureToVisualWorkflow = ({
  workflow,
  dataSources,
  isValidatingFeature
}: ConvertFeatureToVisualWorkflowParams) => {
  let stepCount = 0
  const triggerNode = constructTriggerNode(workflow?.trigger, workflow?.steps[0])
  const triggerNodeHorizontalOffset = 70
  const triggerNodeVerticalOffset = 65
  const triggerPlaceholderNode = constructPlaceholderNode({
    position: {
      x: triggerNode.position.x + triggerNodeHorizontalOffset,
      y: triggerNode.position.y + triggerNodeVerticalOffset
    },
    nextStep: workflow?.steps[0]
  })
  const nodes = [triggerNode, triggerPlaceholderNode] as Node[]
  const addNode = (node: Node) => nodes.push(node)
  const edges = [] as FlowElement[]

  interface AddEdgeParams {
    source: string
    target: string
    label?: string
    type?: string
  }
  const addEdge = ({ source, target, label, type = 'smoothstep' }: AddEdgeParams) => {
    edges.push({
      id: v4(),
      source,
      draggable: false,
      style: { stroke: 'rgba(189,189,189,0.82)', strokeWidth: isValidatingFeature ? 3 : 2 },
      type,
      target,
      animated: isValidatingFeature ? true : false,
      label,
      labelStyle: { fill: '#4b4b4b', fontSize: '1rem', fontWeight: 'bold' }
    })
  }

  addEdge({
    source: triggerNode.id,
    target: triggerPlaceholderNode.id
  })

  interface RecursivelyBuildVisualFlowParams {
    steps: Step[]
    currentStep: Step
    parentStep?: any
    stepType?: string | undefined
  }
  const recursivelyBuildVisualFlow = ({
    steps,
    currentStep,
    parentStep,
    stepType
  }: RecursivelyBuildVisualFlowParams) => {
    const { position, selectedOperation } = getStepOperation({
      elements: nodes,
      step: currentStep,
      steps
    })

    if (selectedOperation) {
      stepCount++

      //Add the step node
      const currentStepNode = constructStepNode(currentStep, position, selectedOperation, stepCount, dataSources)
      addNode(currentStepNode)

      //plot linear path from last placeholder to current step node
      const placeholderNodes = nodes.filter(ele => ele.type === CustomNodes.placeHolderNode)
      addEdge({
        source: placeholderNodes.at(-1)!.id,
        target: currentStepNode.id
      })

      switch (currentStep.type) {
        case StepType.Notification:
        case StepType.NotificationView: {
          const placeholderNodeHorizontalOffset = 20
          let placeholderNodeVerticalOffset = 200
          const notificationData = currentStep.stepData?.notifications?.[0] || currentStep.stepData.data || []
          const notificationHasSummaryImage = !!notificationData?.summary?.image
          const hasLongSummaryTitle =
            notificationData?.summary?.title?.length > 30 || notificationData?.title?.length > 30
          const hasLongSummaryBody = notificationData?.summary?.body?.length > 50 || notificationData?.body?.length > 50

          // Offset following placeholder node if summary title is long, summary body is long , and/or has summary image
          if (notificationHasSummaryImage) {
            if (hasLongSummaryBody && hasLongSummaryTitle) {
              placeholderNodeVerticalOffset += 60
            } else {
              placeholderNodeVerticalOffset += 50
            }
          } else {
            if (hasLongSummaryTitle) {
              placeholderNodeVerticalOffset += 25
            }

            if (hasLongSummaryBody) {
              placeholderNodeVerticalOffset += 30
            }
          }
          if (currentStep.type === StepType.NotificationView) {
            placeholderNodeVerticalOffset += 30
          }

          //Add placeholderNode after the smartNotificationStepNode has been added.
          const placeholder = constructPlaceholderNode({
            type: stepType,
            position: {
              x: currentStepNode.position.x + placeholderNodeHorizontalOffset,
              y: currentStepNode.position.y + placeholderNodeVerticalOffset
            },
            parentStep: parentStep?.id,
            previousStep: currentStep.id,
            nextStep: steps.find(step => step.id === currentStep.onSuccess?.[0])
          })
          addNode(placeholder)
          addEdge({
            source: currentStep.id,
            target: placeholder.id
          })

          break
        }
        case StepType.Boolean: {
          /**
         * Conditional node will plot a diamond on the canvas. One step node and three placeholder nodes or drop targets for more nodes to be added.
         * The boolean Step node is the top node. The onTrue placeholder node is positioned to the left and onTrue steps will be sequentially added on the left.
         * The onFalse placeholder node represents the right middle node and onFalse steps will be sequentially added to the right.
         * The last node at the bottom of the diamond will close the loop on the boolean step logic and will allow the user to drop in a step once the conditions have been finished.

            0

         0     0

            0

         */

          // get the max depth level to be able to determine the position of the conditional placeholder nodes.
          // To prevent overlapping nodes we multiply the depth level by the expected offset of the placeholder node
          const maxNestedNodeCount = getMaxDepthLevel(currentStep)
          const onTruePlaceholderNodeHorizontalOffset = 180
          const onTruePlaceholderNodeVerticalOffset = 220
          const onTruePlaceholderNode = constructPlaceholderNode({
            type: PlaceholderNodeTypes.conditionalTrueStep,
            position: {
              x: currentStepNode.position.x + onTruePlaceholderNodeHorizontalOffset * maxNestedNodeCount,
              y: currentStepNode.position.y + onTruePlaceholderNodeVerticalOffset
            },
            parentStep: currentStep.id,
            nextStep: currentStep.stepData.onTrue[0] || steps.find(step => step.id === currentStep.onSuccess?.[0])
          })
          addEdge({
            source: currentStep.id,
            target: onTruePlaceholderNode.id,
            label: 'True',
            type: 'default'
          })
          addNode(onTruePlaceholderNode)

          const firstOnTrueStep = currentStep.stepData.onTrue?.[0]
          if (firstOnTrueStep) {
            //plot nodes for onTrue array of the boolean step
            recursivelyBuildVisualFlow({
              steps: currentStep.stepData.onTrue,
              currentStep: firstOnTrueStep,
              parentStep: currentStep,
              stepType: PlaceholderNodeTypes.conditionalTrueStep
            })

            addEdge({
              source: onTruePlaceholderNode.id,
              target: firstOnTrueStep.id,
              type: 'default'
            })
          }
          const onFalsePlaceholderNodeHorizontalOffset = -180
          const onFalsePlaceholderNodeVerticalOffset = 220
          const onFalsePlaceholderNode = constructPlaceholderNode({
            type: PlaceholderNodeTypes.conditionalFalseStep,
            position: {
              x: currentStepNode.position.x + onFalsePlaceholderNodeHorizontalOffset * maxNestedNodeCount,
              y: currentStepNode.position.y + onFalsePlaceholderNodeVerticalOffset
            },
            parentStep: currentStep.id,
            nextStep: currentStep.stepData.onFalse[0] || steps.find(step => step.id === currentStep.onSuccess?.[0])
          })

          addEdge({
            source: currentStep.id,
            target: onFalsePlaceholderNode.id,
            label: 'False',
            type: 'default'
          })
          addNode(onFalsePlaceholderNode)

          const firstOnFalseStep = currentStep.stepData.onFalse?.[0]
          if (firstOnFalseStep) {
            //plot nodes for onFalse array of the boolean step
            recursivelyBuildVisualFlow({
              steps: currentStep.stepData.onFalse,
              currentStep: firstOnFalseStep,
              parentStep: currentStep,
              stepType: PlaceholderNodeTypes.conditionalFalseStep
            })

            addEdge({
              source: onFalsePlaceholderNode.id,
              target: firstOnFalseStep.id,
              type: 'default'
            })
          }

          //get the last node in the conditional branch so we can plot the end placeholder node
          const closestStep = nodes
            .filter(ele => ele?.data?.parentStep === currentStep.id)
            .reduce((prev, current) => (prev.position.y > current.position.y ? prev : current))

          const { isLastOnTrueResponseStep, isLastOnFalseResponseStep } = isLastNodeOfTypeResponse(currentStep)
          if (isLastOnTrueResponseStep && isLastOnFalseResponseStep) {
            // do not plot end placeholder node if boolean onTrue and onFalse both end with response steps
            break
          }

          //below is where we need to find the last conditional track (onTrue/onFalse) of the step so we can plot the edge path from that node to the conditional end placeholder
          const endPlaceholderNodeVerticalOffset = 100
          const conditionalEndPlaceholder = constructPlaceholderNode({
            type: stepType,
            position: {
              x: currentStepNode.position.x,
              y: closestStep.position.y + endPlaceholderNodeVerticalOffset
            },
            parentStep: parentStep?.id,
            previousStep: currentStep.id,
            nextStep: steps.find(step => step.id === currentStep.onSuccess?.[0])
          })
          const lastOnFalseStep = getLastNestedPlaceholderNode(currentStep, nodes, onFalsePlaceholderNode, 'onFalse')
          if (lastOnFalseStep) {
            addEdge({
              source: lastOnFalseStep.id,
              target: conditionalEndPlaceholder.id,
              type: 'default'
            })
          }
          const lastOnTrueStep = getLastNestedPlaceholderNode(currentStep, nodes, onTruePlaceholderNode, 'onTrue')
          if (lastOnTrueStep) {
            addEdge({
              source: lastOnTrueStep.id,
              target: conditionalEndPlaceholder.id,
              type: 'default'
            })
          }

          if (lastOnFalseStep || lastOnTrueStep) {
            addNode(conditionalEndPlaceholder)
          }

          break
        }
        case StepType.Loop: {
          /**
         * Loop node will plot a half diamond on the canvas. One step node and two placeholder nodes or drop targets for more nodes to be added.
         * The onLoop placeholder node represents the right middle node and loop steps will be sequentially added to the right.
         * The last node at the bottom of the diamond will close the loop logic and will allow the user to drop in a step once the conditions have been finished.

         0

             0

         0

         */

          // get the max depth level to be able to determine the position of the conditional placeholder nodes.
          // To prevent overlapping nodes we multiply the depth level by the expected offset of the placeholder node
          const maxNestedNodeCount = getMaxDepthLevel(currentStep)

          const onLoopPlaceholderNodeHorizontalOffset = 100
          const onLoopPlaceholderNodeVerticalOffset = 140
          const onLoopPlaceholderNode = constructPlaceholderNode({
            type: PlaceholderNodeTypes.loopStep,
            position: {
              x: currentStepNode.position.x + onLoopPlaceholderNodeHorizontalOffset * maxNestedNodeCount,
              y: currentStepNode.position.y + onLoopPlaceholderNodeVerticalOffset
            },
            parentStep: currentStep.id,
            nextStep: steps.find(step => step.id === currentStep.onSuccess?.[0])
          })

          addEdge({
            source: currentStep.id,
            target: onLoopPlaceholderNode.id,
            label: 'Start Loop'
          })
          addNode(onLoopPlaceholderNode)

          const firstOnLoopStep = currentStep.stepData.loopSteps?.[0]
          if (firstOnLoopStep) {
            //plot nodes for loopSteps array of the loop step
            recursivelyBuildVisualFlow({
              steps: currentStep.stepData.loopSteps,
              currentStep: firstOnLoopStep,
              parentStep: currentStep,
              stepType: PlaceholderNodeTypes.loopStep
            })

            addEdge({
              source: onLoopPlaceholderNode.id,
              target: firstOnLoopStep.id
            })
          }

          //get the last node in the conditional branch so we can plot the end placeholder node
          const closestStep = nodes
            .filter(ele => ele?.data?.parentStep === currentStep.id)
            .reduce((prev, current) => (prev.position.y > current.position.y ? prev : current))

          const endPlaceholderNodeVerticalOffset = 100
          const loopEndPlaceholder = constructPlaceholderNode({
            type: stepType,
            position: {
              x: currentStepNode.position.x,
              y: closestStep.position.y + endPlaceholderNodeVerticalOffset
            },
            parentStep: parentStep?.id,
            previousStep: currentStep.id,
            nextStep: steps.find(step => step.id === currentStep.onSuccess?.[0])
          })

          //below is where we need to find the last conditional track (onTrue/onFalse) of the step so we can plot the edge path from that node to the conditional end placeholder
          const lastLoopStep = getLastNestedPlaceholderNode(currentStep, nodes, onLoopPlaceholderNode, 'loopSteps')
          if (lastLoopStep) {
            addEdge({
              source: lastLoopStep.id,
              target: loopEndPlaceholder.id,
              label: 'End Loop'
            })

            addNode(loopEndPlaceholder)
          }

          break
        }
        case StepType.Response: {
          // Terminal nodes should not be connected to a placeholder
          break
        }
        default: {
          //all other operations with a linear path
          const placeholder = constructPlaceholderNode({
            type: stepType,
            position: { x: currentStepNode.position.x, y: currentStepNode.position.y },
            parentStep: parentStep?.id,
            previousStep: currentStep.id,
            nextStep: steps.find(step => step.id === currentStep.onSuccess?.[0])
          })
          addNode(placeholder)

          addEdge({
            source: currentStep.id,
            target: placeholder.id
          })
        }
      }
    }
    const nextSuccessStep = steps.find(step => step.id === currentStep.onSuccess?.[0])
    if (nextSuccessStep) {
      recursivelyBuildVisualFlow({
        steps,
        currentStep: nextSuccessStep,
        parentStep,
        stepType
      })
    }
  }

  if (workflow?.steps && workflow?.steps?.length > 0) {
    recursivelyBuildVisualFlow({
      steps: workflow.steps,
      currentStep: workflow.steps[0]
    })
  }

  return [...nodes, ...edges]
}
